import React, { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalTeams.module.sass";
import Icon from "../Icon";
import Card from "../Card";
import TextInput from "../TextInput";
import Dropdown from "../Dropdown";
import { createMember, updateUser } from "../../services/user.service";
import { showSuccess, showError } from "../../services/toast.service";


const ROLES = ["Admin", "Commercial", "Pedagogical", "Teacher"];

const ModalTeams = ({ visible, onClose, member }) => {
  const [formData, setFormData] = useState(() => ({
    first_name: "",
    last_name: "",
    email: "",
    role: ROLES[0],
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modalRef = useRef(null);

  // Gère le défilement du corps et l'initialisation des données du formulaire
  useEffect(() => {
    const targetElement = modalRef.current || document.querySelector("#modal-member");

    if (visible) {
      if (targetElement) {
        disableBodyScroll(targetElement);
      }
      setFormData({
        first_name: member?.first_name || "",
        last_name: member?.last_name || "",
        email: member?.email || "",
        role: Array.isArray(member?.role) ? member.role[0] : member?.role || ROLES[0],
      });
      setIsSubmitting(false); // Réinitialise l'état de soumission à l'ouverture de la modale
    } else {
      clearAllBodyScrollLocks();
      setFormData({ // Réinitialise les données du formulaire à la fermeture
        first_name: "",
        last_name: "",
        email: "",
        role: ROLES[0],
      });
    }

    // Fonction de nettoyage pour déverrouiller le défilement du corps
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [visible, member]);

  // Gère la touche 'Échap' pour fermer la modale
  const escFunction = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  // Ajoute et retire l'écouteur d'événements clavier
  useEffect(() => {
    document.addEventListener("keydown", escFunction);
    return () => {
      document.removeEventListener("keydown", escFunction);
    };
  }, [escFunction]);

  // Gère les changements des champs de texte
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // Gère le changement de rôle dans le Dropdown
  const handleRoleChange = useCallback((roleValue) => {
    setFormData((prev) => ({
      ...prev,
      role: roleValue,
    }));
  }, []);

  // Gère la soumission du formulaire (création ou mise à jour de membre)
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      try {
        if (member?.id) {
          // Met à jour un utilisateur existant
          await updateUser(member.id, formData);
          showSuccess("Utilisateur mis à jour avec succès !");
        } else {
          // Crée un nouvel utilisateur
          await createMember(formData);
          showSuccess("Utilisateur ajouté avec succès !");
        }
        onClose(); // Ferme la modale en cas de succès
      } catch (error) {
        console.error("Erreur lors de l'opération sur le membre:", error);
        showError(error.message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, member, onClose]
  );

  // Ne rend le portail que si la modale est visible
  if (!visible) {
    return null;
  }

  return createPortal(
    <div id="modal-member" className={styles.modal} ref={modalRef}>
      <div className={styles.control}>
        <button className={styles.close} onClick={onClose} aria-label="Fermer la modale">
          <Icon name="close" size="20" />
        </button>
      </div>
      <div className={styles.outer}>
        <Card
          className={styles.card}
          title={member ? "Modifier le membre" : "Ajouter un membre"}
          classTitle="title-blue"
        >
          <div className={styles.container}>
            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.group}>
                <TextInput
                  className={styles.field}
                  label="Nom"
                  name="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
                <TextInput
                  className={styles.field}
                  label="Prénom"
                  name="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <TextInput
                className={styles.field}
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />


              <div className={styles.divider} />

              <Dropdown
                className={styles.Dropdown}
                label="Rôle"
                value={formData.role}
                setValue={handleRoleChange}
                options={ROLES}
              />

              <div className={styles.divider} />
              <div className={styles.btnWrap}>
                <button
                  type="submit"
                  className="button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sauvegarde..." : (member ? "Sauvegarder" : "Ajouter le membre")}
                </button>
              </div>
            </form>
          </div>
        </Card>
      </div>
    </div>,
    document.body
  );
};

export default ModalTeams;